import RenderIf from 'app/components/RenderIf';
import _ from 'lodash';
import React, { useMemo } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  // Line,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import styled from 'styled-components';
import { DEFAULT_LANGUAGE, TYPE_COOKIE } from 'utils/constants';
import { getCookie } from 'utils/cookies';
import { formatNumber } from 'utils/helper';

interface IDataRevenue {
  name?: string;
  revenue: number;
  ticket: number;
  month: number;
  ticketCount?: number;
}
interface PieTooltipPayload {
  name: string;
  value: number;
  dataKey: string;
  payload: {
    fill?: string;
  };
}
interface PieTooltipProps {
  active?: boolean;
  payload?: PieTooltipPayload[];
  translations: any;
}
const CustomTooltip = ({ active, payload, translations }: PieTooltipProps) => {
  if (!active || !payload?.length) return null;
  const revenue = payload.find(p => p.dataKey === 'revenue');
  const ticket = payload.find(p => p.dataKey === 'ticket');
  const revenueValue = Number(revenue?.value ?? 0);
  const ticketValue = Number(ticket?.value ?? 0);
  const isNoData =
    (!revenue && !ticket) || (revenueValue === 0 && ticketValue === 0);
  if (isNoData) {
    return (
      <TooltipWrapper>
        <div className="text-ticket">{translations?.['b2b.txt.noData']}</div>
      </TooltipWrapper>
    );
  }
  return (
    <TooltipWrapper>
      {ticketValue !== 0 && (
        <StyledRow>
          <StyledLine />
          <div className="text-ticket">
            {translations?.['b2b.txt.ticketQuantity']}:
          </div>
          <span className="text-number-ticket">
            {ticketValue.toLocaleString()}{' '}
            <span>{translations?.['b2b.txt.ticket']}</span>
          </span>
        </StyledRow>
      )}
      {revenueValue !== 0 && (
        <StyledRow>
          <StyledDot />
          <div className="text-ticket">
            {translations?.['b2b.txt.revenueChart']}:
          </div>
          <span className="text-number-ticket">
            {formatNumber(revenueValue, '.')} <span>đ</span>
          </span>
        </StyledRow>
      )}
    </TooltipWrapper>
  );
};
interface CustomLegendProps {
  payload?: any[];
}
const CustomLegend = ({ payload }: CustomLegendProps) => {
  return (
    <ul
      style={{
        listStyle: 'none',
        display: 'flex',
        padding: 0,
        justifyContent: 'center',
        gap: 20,
      }}
    >
      {payload
        ?.slice()
        .reverse()
        .map((entry, index: number) => (
          <li
            key={`item-${index}`}
            style={{
              marginRight: 20,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            {entry.type === 'line' ? (
              <div
                style={{
                  width: 12,
                  height: 4,
                  backgroundColor: entry.color,
                  borderRadius: 6,
                }}
              />
            ) : (
              <div
                style={{
                  width: 8,
                  height: 8,
                  backgroundColor: entry.color,
                  borderRadius: 8,
                }}
              />
            )}
            <span>{entry.value}</span>
          </li>
        ))}
    </ul>
  );
};
interface IRevenuesChartProps {
  translations: any;
  data: IDataRevenue[];
  isLoading: boolean;
}
const getMonthLabel = (month: number, locale: 'vi' | 'en') => {
  const date = new Date(2025, month - 1, 1);
  return new Intl.DateTimeFormat(locale, { month: 'short' }).format(date);
};
const formatCurrencyShort = (value: number): string => {
  const units = [
    { threshold: 1000000000, suffix: 'B' },
    { threshold: 1000000, suffix: 'M' },
    { threshold: 1000, suffix: 'K' },
  ];
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  for (const unit of units) {
    if (absValue >= unit.threshold) {
      const num = absValue / unit.threshold;
      const rounded = Math.round(num * 10) / 10;
      return `${sign}${rounded}${unit.suffix}`;
    }
  }
  return `${sign}${absValue}`;
};
export function RevenueReportChart(props: IRevenuesChartProps) {
  const { translations, data } = props;
  const locale = getCookie(TYPE_COOKIE.LANGUAGE) || DEFAULT_LANGUAGE;
  const chartData = useMemo(
    () =>
      data.map(item => ({
        name: getMonthLabel(item.month, locale),
        revenue: item.revenue ?? 0,
        ticketCount: item.ticketCount ?? 0,
      })),
    [data, locale],
  );
  return (
    <RenderIf
      condition={!_.isEmpty(chartData)}
      fallback={
        <div className="box-no-order" style={{ height: 350 }}>
          {translations?.['b2b.txt.haveNoData']}
        </div>
      }
    >
      <ResponsiveContainer width="100%" height={350}>
        <ComposedChart data={chartData}>
          <ReferenceArea
            x1={chartData?.[0]?.name}
            x2={chartData?.[chartData?.length - 1]?.name}
            fill="white"
            fillOpacity={1}
          />
          <CartesianGrid stroke="#eaeaea" />
          <XAxis
            dataKey="name"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fontWeight: 600 }}
          />
          <YAxis
            orientation="left"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12 }}
            tickFormatter={value =>
              value === 0 ? '(VND)' : formatCurrencyShort(value)
            }
          />
          {/* <YAxis
            yAxisId="left"
            orientation="left"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12 }}
            tickFormatter={value =>
              value === 0 ? '(vé)' : formatNumber(value, '.')
            }
          /> */}
          <Tooltip content={<CustomTooltip translations={translations} />} />
          <Legend content={<CustomLegend />} />
          <Bar
            dataKey="revenue"
            fill="#1651D0"
            radius={[4, 4, 0, 0]}
            barSize={30}
            name={translations?.['b2b.txt.revenueChart']}
          />
          {/* <Line
            yAxisId="left"
            type="linear"
            dataKey="ticket"
            stroke="#91C464"
            strokeWidth={2}
            dot={{ r: 3, fill: '#fff', stroke: '#91C464' }}
            activeDot={{ r: 5 }}
            name={translations?.['b2b.txt.ticketQuantity']}
          /> */}
        </ComposedChart>
      </ResponsiveContainer>
    </RenderIf>
  );
}
const TooltipWrapper = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 12px;
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2);
  .text-ticket {
    color: var(--color-12);
  }
  .text-number-ticket {
    font-weight: 600;
  }
`;
const StyledRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;
const StyledLine = styled.div`
  height: 4px;
  width: 12px;
  border-radius: 12px;
  background-color: #91c464;
`;
const StyledDot = styled.div`
  height: 8px;
  width: 8px;
  border-radius: 8px;
  background-color: #1651d0;
`;
